import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { DepositInvoiceConfig } from '../../../types/invoiceDeposit/depositInvoiceConfig'
import { DepositInvoiceDetailsVatLine } from '../../../types/invoiceDeposit/depositInvoiceDetailsVatLine'
import { calculateDepositInvoiceRemaining } from '../../../utils/depositInvoices/calculateDepositInvoiceRemaining'
import { DepositDetailsFormSchema } from '../types/depositDetailsFormSchema'
import { DepositInvoiceDetailsForm } from '../types/depositInvoiceDetailsForm'
import { getDepositInvoiceDetailsFormDefaultValues } from '../utils/getDepositInvoiceDetailsFormDefaultValues'
import { getDepositInvoiceDetailsFormSchema } from '../utils/getDepositInvoiceDetailsFormSchema'
import { useDepositInvoiceDetailsModal } from './depositInvoiceDetailsModalContext'

interface ContextState {
  depositDetailsForm: UseFormReturn<DepositDetailsFormSchema>
  isEditMode: boolean
  predefinedValues?: DepositInvoiceConfig
  quoteAmount: number
  remainingBalance: number
  submitForm: () => void
  vatLines: DepositInvoiceDetailsVatLine[]
}

const DepositInvoiceDetailsFormContext = createContext<ContextState | undefined>(undefined)

interface DepositDetailsFormProviderProps {
  children?: ReactNode
  onSubmit: (formData: DepositInvoiceDetailsForm) => void
  predefinedValues?: DepositInvoiceConfig
  quoteAmount: number
}

export const DepositInvoiceDetailsFormContextProvider = ({
  children,
  onSubmit,
  quoteAmount,
  predefinedValues,
}: DepositDetailsFormProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { quoteCurrency, quoteLines } = useDepositInvoiceDetailsModal()

  const depositDetailsForm = useForm<DepositDetailsFormSchema>({
    defaultValues: getDepositInvoiceDetailsFormDefaultValues(predefinedValues),
    validationSchema: getDepositInvoiceDetailsFormSchema(t, quoteAmount),
  })

  const { Form, handleSubmit, setError, watch } = depositDetailsForm
  const [depositAmount, depositType] = watch(['amount', 'type'])

  const { remainingBalance, vatLines } = useMemo(
    () =>
      calculateDepositInvoiceRemaining({
        currency: quoteCurrency,
        depositAmount,
        quoteAmount,
        quoteLines,
        depositType,
      }),
    [quoteCurrency, quoteLines, quoteAmount, depositAmount, depositType],
  )

  const submitForm = useCallback(() => {
    handleSubmit((formData) => {
      if (!vatLines.length) {
        setError('amount', {
          message: t('external_invoices.deposit_invoice_details_modal.form.amount.calculation_error'),
        })
        return
      }

      onSubmit(formData)
    })()
  }, [handleSubmit, onSubmit, setError, t, vatLines])

  const contextValue = useMemo(
    () => ({
      depositDetailsForm,
      isEditMode: !!predefinedValues,
      predefinedValues,
      quoteAmount,
      remainingBalance,
      submitForm,
      vatLines,
    }),
    [depositDetailsForm, predefinedValues, quoteAmount, remainingBalance, submitForm, vatLines],
  )

  return (
    <DepositInvoiceDetailsFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </DepositInvoiceDetailsFormContext.Provider>
  )
}

export const useDepositInvoiceForm = () => {
  const context = useContext(DepositInvoiceDetailsFormContext)

  if (!context) {
    throw new Error('DepositInvoiceDetailsFormContextProvider is missing in the module!')
  }

  return context
}
